import { type Change, type ChangeFlags, generateKey } from '@gitbook/slate';
import type { DeepReadonly } from '@gitbook/util-immutable';
import { createOrderIndex, type OrderIndexPosition } from '@gitbook/util-orderindex';

import type { BlockTableData, TableColumnDefinition, TableView } from '../types';
import { createColumnFromType, getLastColumnIndex, getLastRecordOrderIndex } from './utils';

/**
 * Insert a column in a table.
 */
export function insertColumn(
    change: Change,
    tableKey: string,

    /**
     * Column definition to insert.
     */
    definition: TableColumnDefinition = createColumnFromType('text'),

    /**
     * Index in the view.columns array at which to insert the new visible column.
     * If none is provided, the column will be inserted at the end.
     */
    atIndex?: number
) {
    const index =
        typeof atIndex === 'number'
            ? atIndex
            : getLastColumnIndex(
                  change.value.document
                      .assertBlock(tableKey)
                      .data.toFrozenObject() as BlockTableData
              ) + 1;

    change.insertDataByKey(tableKey, ['definition', definition.id], definition, {
        normalize: false,
    });

    // Insert the column at the correct position
    change.insertArrayDataByKey(tableKey, ['view', 'columns'], definition.id, index);
}

/**
 * Insert a new empty record into a table. It returns the ID of the newly created record.
 */
export function insertEmptyRecord(
    change: Change,
    tableKey: string,
    at?: OrderIndexPosition
): string {
    const id = generateKey();

    // Insert an empty object, normalization will take care with the rest
    change.insertDataByKey(tableKey, ['records', id], {
        values: {},
        orderIndex: createOrderIndex(
            at
                ? at
                : {
                      before: getLastRecordOrderIndex(
                          change.value.document
                              .assertBlock(tableKey)
                              .data.toFrozenObject() as BlockTableData
                      ),
                  }
        ),
    });

    return id;
}

/**
 * Move a record to another position.
 */
export function moveRecord(
    change: Change,
    tableKey: string,
    recordId: string,
    at: OrderIndexPosition
) {
    change.setDataByKey(tableKey, ['records', recordId, 'orderIndex'], createOrderIndex(at));
}

/**
 * Move a column in the grid or cards view.
 * Accepts the id of the column to move and the id of the column where it will be dropped.
 */
export function moveColumn({
    change,
    tableKey,
    columnId,
    beforeColumnId,
}: {
    change: Change;
    tableKey: string;
    columnId: string;
    beforeColumnId: string;
}) {
    const view = change.value.document.assertBlock(tableKey).data.get('view') as TableView;
    const { columns } = view;

    const fromIndex = columns.indexOf(columnId);
    const beforeIndex = columns.indexOf(beforeColumnId);
    if (fromIndex < 0 || beforeIndex < 0) {
        return;
    }

    const newIndex =
        fromIndex < beforeIndex || view.type === 'cards' ? beforeIndex : beforeIndex + 1;

    change
        .removeArrayDataByKey(tableKey, ['view', 'columns'], columnId, { normalize: false })
        .insertArrayDataByKey(tableKey, ['view', 'columns'], columnId, newIndex);
}

/**
 * Hide a column from a view.
 */
export function hideColumn(change: Change, tableKey: string, columnId: string) {
    change.removeArrayDataByKey(tableKey, ['view', 'columns'], columnId);
}

/**
 * Show a previously hidden column in a table.
 */
export function showColumn({
    change,
    tableKey,
    columnId,
    atIndex,
}: {
    change: Change;
    tableKey: string;
    columnId: string;
    atIndex?: number;
}) {
    const table = change.value.document.assertBlock(tableKey);
    const definition = table.data.get('definition');
    const originalColumn = definition[columnId];

    if (!originalColumn) return;

    // Calculate insert position if not provided
    const view = table.data.get('view');
    const insertIndex = typeof atIndex === 'number' ? atIndex : view.columns.indexOf(columnId) + 1;

    // Insert the column at the correct position
    change.insertArrayDataByKey(tableKey, ['view', 'columns'], columnId, insertIndex);
}

/**
 * Remove a column from a table.
 */
export function removeColumn(change: Change, tableKey: string, columnId: string) {
    // Remove it from the definition, let validation take care of the rest.
    change.removeDataPathByKey(tableKey, ['definition', columnId]);
}

/**
 * Change the type of a column.
 */
export function setColumnDefinition(
    change: Change,
    tableKey: string,
    columnId: string,
    def: TableColumnDefinition,
    options?: ChangeFlags
) {
    change.setDataByKey(tableKey, ['definition', columnId], { ...def, id: columnId }, options);
}

/**
 * Removes a record from the table.
 */
export function removeRecord(change: Change, tableKey: string, recordId: string) {
    change.removeDataPathByKey(tableKey, ['records', recordId]);
}

/**
 * Sets the value of a record for a specific column.
 */
export function setRecordValue(
    change: Change,
    tableKey: string,
    recordId: string,
    colId: string,
    value: any,
    options?: ChangeFlags
) {
    change.insertDataByKey(tableKey, ['records', recordId, 'values', colId], value, options);
}

/**
 * Sets the value of a record for a specific column.
 */
export function renameColumn(change: Change, tableKey: string, columnId: string, title: string) {
    change.insertDataByKey(tableKey, ['definition', columnId, 'title'], title);
}

/**
 * Change the view in a table.
 */
export function setView(
    change: Change,
    tableKey: string,
    view: Partial<DeepReadonly<TableView>> & Pick<TableView, 'type'>
) {
    const currentView = change.value.document.assertBlock(tableKey).data.get('view') as TableView;

    // Automatically preserve all existing view properties unless explicitly overridden
    const updatedView = {
        ...currentView,
        ...view,
        columns: view.columns || currentView.columns, // Ensure columns are preserved
    };

    change.setDataByKey(tableKey, ['view'], updatedView);
}
